/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomScrollerTable.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import AddButtonV2 from "../button/AddButtonV2";

function CustomScrollerTable({
  count = "",
  title = "Vehicle Function",
  columns = [],
  rows = [],
  handleOnClick,
  onlyUsers,
  hideAddButton = false,
  actionIcon,
}) {
  console.log("onlyUsers", onlyUsers);
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // 🧠 Track scroll progress and update bar height
  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const progress = Math.min(
        (scrollTop / (scrollHeight - clientHeight)) * 100,
        100,
      );
      setScrollProgress(progress);
    };

    const container = containerRef.current;
    if (container) container.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        borderRadius: 0,
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* Wizard Progress Bar */}
      <Box
        sx={{
          position: "relative",
          width: "6px",

          backgroundColor: "#f0f0f0",
          flexShrink: 0,
        }}
      >
        {/* Dynamic Green Fill (TOP → DOWN) */}
        <Box
          sx={{
            position: "relative",
            height: "25%",
          }}
        >

        </Box>
      </Box>

      {/* Right Side (Header + Table) */}
      <Box sx={{ flex: 1, ml: 1, border: "1px solid #ddd" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              display: "flex",
              fontWeight: 600,
              color: "#0b0b0b",
              letterSpacing: 0.3,
            }}
          >
            {title}
            {count !== "" ? (
              <span
                style={{
                  backgroundColor: "#D0D0CE",
                  color: "black",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "5px",
                  marginLeft: 3,
                }}
              >
                {count}
              </span>
            ) : null}
          </Typography>

          {!hideAddButton && title !== "Zones" && title !== "Users" ? (

            <Box>
              <AddButtonV2 handleOnClick={handleOnClick} icon={actionIcon} />
            </Box>
          ) : null}
        </Box>

        {/* Scrollable Table */}
        <TableContainer
          ref={containerRef}
          component={Paper}
          sx={{
            boxShadow: "none",
            borderRadius: 0,
            maxHeight: 250,
            minHeight: 100,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#a0a0a0",
            },
          }}
        >
          {title?.toUpperCase() === "USERS" ? (
            <Box>{onlyUsers}</Box>
          ) : (
            <Table
              stickyHeader
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        fontWeight: 600,
                        color: "#333",
                        backgroundColor: "#fafafa",
                        borderBottom: "1px solid #ddd",
                        textTransform: "capitalize",
                        whiteSpace: "nowrap", // ✅ keeps header text on one line
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: col.width || "150px", // ✅ fixed width (customizable per column)
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        height: 40,
                        "&:hover": { backgroundColor: "#f9f9f9" },
                        "& td": {
                          borderBottom: "1px solid #eee",
                          py: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{ width: col.width || "150px" }}
                        >
                          {row[col.id] ?? "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 2 }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
}

export default CustomScrollerTable;
